import { useState, useEffect, useRef } from "react";
import { Modal } from "../../ui/modal";
import { ChevronDownIcon, CloseIcon, TrashBinIcon } from "../../../icons";
import Button from "../../ui/button/Button";
import Spinner from "../../ui/Spinner/Spinner";
import {
  PaymentDetails,
  ExchangeRateResponse,
  Currency,
} from "../../Transactions/types/Exchange";
import {
  fetchMobileNetworks,
  fetchBanks,
} from "../../Transactions/Apis/PaymentMethods";
import axios from "axios";
import { cache } from "../../../utils";
import { CircleCheck } from "lucide-react";
import {
  EMAIL_PAYMENT_METHODS,
  getBankCodeMaxLength,
  INT_BANK_FIELD_CONFIG,
  INTERNATIONAL_BANK_ISO2,
  isBankTransferMethod,
  PAYMENT_METHOD,
  PAYMENT_METHOD_LABELS,
} from "../../../utils/constant";
// import { CURRENCIES } from "@monirates/utils/dist/utils/currency.js";
import {
  buildApiPayload,
  getBlockchainName,
  getNetworkCode,
} from "../../../utils/helpers";

interface PayBankDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (details: PaymentDetails) => void;
  currency: string;
  paymentMethod: string;
  exchangeRate?: ExchangeRateResponse;
  fromCurrency?: Currency;
  hasBankList?: boolean;
  compareSelected?: {
    accountName?: string;
    accountNumber?: string;
    providerName?: string;
    email?: string;
    walletAddress?: string;
    network?: string;
    holderCity?: string;
    holderStreet?: string;
    holderPostalCode?: string;
    phoneNumber?: string;
  };
  allowedMomoProviders?: string[];
}

interface DelPaymentDetails {
  accountName?: string;
  accountNumber?: string;
  providerName?: string;
  paymentMethod: string;
  iso2: string;
  currency: string;
  bankCode?: string;
  network?: string;
  saveDetails?: boolean;
  bankCodeLabel?: string;
  email?: string;
  _id?: string;
  walletAddress?: string;
  holderCity?: string;
  holderStreet?: string;
  holderPostalCode?: string;
  phoneNumber?: string;
}

export default function PayBankDetailsModal({
  isOpen,
  onClose,
  onSave,
  currency,
  hasBankList,
  paymentMethod,
  exchangeRate,
  fromCurrency,
  compareSelected,
  // allowedMomoProviders
}: PayBankDetailsModalProps) {
  const [step, setStep] = useState<"select" | "form">("select");
  const [details, setDetails] = useState<PaymentDetails>({
    accountName: "",
    accountNumber: "",
    providerName: "",
    paymentMethod: paymentMethod,
    iso2: fromCurrency?.iso2 || "",
    currency: currency,
    bankCode: "",
    saveDetails: false,
    email: "",
    network: "",
    walletAddress: "",
    holderCity: "",
    holderStreet: "",
    holderPostalCode: "",
    phoneNumber: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [savedBeneficiaries, setSavedBeneficiaries] = useState<
    PaymentDetails[]
  >([]);
  const [providers, setProviders] = useState<any[]>([]);
  const [filteredBanks, setFilteredBanks] = useState<any[]>([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [accountNameVerified, setAccountNameVerified] = useState(false);
  const [isVerifyingAccount, setIsVerifyingAccount] = useState(false);
  const [bankSearchQuery, setBankSearchQuery] = useState("");
  const [showBankDropdown, setShowBankDropdown] = useState(false);
  const [showProviderDropdown, setShowProviderDropdown] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [paymentMethodToDelete, setPaymentMethodToDelete] =
    useState<DelPaymentDetails | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const bankSearchRef = useRef<HTMLDivElement>(null);
  const providerDropdownRef = useRef<HTMLDivElement>(null);
  const [isCurrencyNotSupportedModalOpen, setIsCurrencyNotSupportedModalOpen] =
    useState(false);
  const [paymentNetworkError, setPaymentNetworkError] = useState("");

  // ---- Reset Form
  const resetForm = () => {
    setDetails({
      accountName: "",
      accountNumber: "",
      providerName: "",
      paymentMethod: paymentMethod || "",
      iso2: fromCurrency?.iso2 || "",
      currency: currency,
      saveDetails: false,
      bankCode: "",
      network: "",
      email: "",
      walletAddress: "",
      holderCity: "",
      holderStreet: "",
      holderPostalCode: "",
      phoneNumber: "",
    });
    setAccountNameVerified(false);
    setError("");
    setStep("select");
    setBankSearchQuery("");
    setFilteredBanks([]);
    setPaymentNetworkError("");
  };
  // ---- Close
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // ---- Use Effect for drop downs
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        bankSearchRef.current &&
        !bankSearchRef.current.contains(event.target as Node)
      ) {
        setShowBankDropdown(false);
      }
      if (
        providerDropdownRef.current &&
        !providerDropdownRef.current.contains(event.target as Node)
      ) {
        setShowProviderDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // ---- Fetch saved payment depending on the country
  const fetchSavedPaymentMethods = async () => {
    try {
      const token = cache.get("TOKEN");
      const iso2 = fromCurrency?.iso2 || "";
      if (!iso2) return;

      const response = await axios.get(
        `${
          import.meta.env.VITE_BASE_URL
        }/buyer-exchange-profile/${iso2}/payment-details`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data && response.data.success) {
        setSavedBeneficiaries(response.data.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch saved payment methods:", error);
      setError("Failed to load saved payment methods");
    }
  };

  // ---- Delete payment method
  const deletePaymentMethod = async () => {
    if (!paymentMethodToDelete || !fromCurrency?.iso2) return;

    setIsDeleting(true);
    try {
      const token = cache.get("TOKEN");

      const response = await axios.delete(
        `${
          import.meta.env.VITE_BASE_URL
        }/buyer-exchange-profile/payment-details/${paymentMethodToDelete._id}/${
          fromCurrency.iso2
        }`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data && response.data.success) {
        // Refresh the list after successful deletion
        await fetchSavedPaymentMethods();
        setIsDeleteModalOpen(false);
        setPaymentMethodToDelete(null);
      }
    } catch (error) {
      console.error("Failed to delete payment method:", error);
      setError("Failed to delete payment method");
    } finally {
      setIsDeleting(false);
    }
  };

  // ---- Open delete confirmation modal
  const openDeleteModal = (
    beneficiary: PaymentDetails,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    setPaymentMethodToDelete(beneficiary);
    setIsDeleteModalOpen(true);
  };

  // ---- Save payment details when checkbox is checked
  // const savePaymentDetails = async (paymentDetails: DelPaymentDetails) => {
  //   try {
  //     const token = cache.get("TOKEN");

  //     const { saveDetails: _, ...raw } = paymentDetails;

  //     const apiPayload = buildApiPayload(raw);

  //     const response = await axios.post(
  //       `${
  //         import.meta.env.VITE_BASE_URL
  //       }/buyer-exchange-profile/payment-details`,
  //       apiPayload,
  //       {
  //         headers: {
  //           Authorization: `Bearer ${token}`,
  //         },
  //       },
  //     );

  //     if (response.data && response.data.success) {
  //       // Refresh the saved payment methods after saving
  //       await fetchSavedPaymentMethods();
  //     }
  //   } catch (error) {
  //     console.error("Failed to save payment details:", error);
  //     setError("Failed to save payment details");
  //   }
  // };

    const savePaymentDetails = async (paymentDetails: DelPaymentDetails): Promise<boolean> => {
      try {
        const token = cache.get("TOKEN");
        const { saveDetails: _, ...raw } = paymentDetails;
        const apiPayload = buildApiPayload(raw);
  
        const response = await axios.post(
          `${import.meta.env.VITE_BASE_URL}/buyer-exchange-profile/payment-details`,
          apiPayload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
  
        if (response.data && response.data.success) {
          await fetchSavedPaymentMethods();
          return true;  // ← added
        }
  
        setError("Failed to save payment details");  // ← added
        return false;  // ← added
      } catch (error: any) {  // ← any added
        console.error("Failed to save payment details:", error);
        setError(error?.response?.data?.message || "Failed to save payment details");  // ← better error message
        return false;  // ← added
      }
    };

  // ---- Use Effect to fetch saved payment details and deterine which field to show depending on iso2
  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      setError("");
      const token = cache.get("TOKEN");
      if (token) {
        fetchSavedPaymentMethods();
      } else {
        console.error("User not authenticated yet");
      }

      if (paymentMethod) {
        if (paymentMethod === PAYMENT_METHOD.MOBILE_MONEY) {
          fetchPaymentNetworks();
        } else if (isBankTransferMethod(paymentMethod)) {
          if (hasBankList) {
            fetchBanksList();
          }
        }
      }
      setIsLoading(false);
    } else {
      resetForm();
    }
  }, [isOpen, paymentMethod, currency, details.iso2]);

  // ---- useEffect for searching bank and providing the search results in real time
  useEffect(() => {
    if (bankSearchQuery && providers.length > 0) {
      const filtered = providers
        .filter((bank) =>
          bank.name.toLowerCase().includes(bankSearchQuery.toLowerCase()),
        )
        .slice(0, 10);
      setFilteredBanks(filtered);
    } else {
      setFilteredBanks([]);
    }
  }, [bankSearchQuery, providers]);

  // ---- fetch payment networks for mobile money
  const fetchPaymentNetworks = async () => {
    setLoadingProviders(true);
    try {
      const mobileMoneyDetail = exchangeRate?.paymentDetails?.find(
        (detail) => detail.paymentMethod === PAYMENT_METHOD.MOBILE_MONEY,
      );

      const iso2 =
        mobileMoneyDetail?.iso2 ||
        fromCurrency?.iso2 ||
        (fromCurrency?.code ? fromCurrency.code.substring(0, 2) : "");

      if (!iso2) {
        throw new Error("Could not determine country code");
      }

      const providersData = await fetchMobileNetworks(currency, iso2);
      //  let providers;

      // if (Array.isArray(allowedMomoProviders) && allowedMomoProviders.length > 0) {
      //   providers = providersData.filter((provider: any) =>
      //     allowedMomoProviders.includes(provider.name)
      //   );
      // } else {
      //   providers = providersData;
      // }

      setProviders(providersData);
    } catch (error: any) {
      console.error("Failed to fetch mobile networks:", error);
      setPaymentNetworkError(error.response.data.message);
    } finally {
      setLoadingProviders(false);
    }
  };

  // ---- fetch bank list for NGN currency
  const fetchBanksList = async () => {
    setLoadingProviders(true);
    try {
      const banksData = await fetchBanks(currency);
      setProviders(banksData);
    } catch (error) {
      console.error("Failed to fetch banks:", error);
      setError("Failed to load banks");
    } finally {
      setLoadingProviders(false);
    }
  };

  // ---- verify bank account using selected bank and account number
  const verifyBankAccount = async () => {
    if (!details.bankCode || !details.accountNumber) return;

    setIsVerifyingAccount(true);
    setError("");

    try {
      const token = cache.get("TOKEN");
      const response = await axios.post(
        `${import.meta.env.VITE_BASE_URL}/transaction/verify-bank-account`,
        {
          currency: "NGN",
          bankCode: details.bankCode,
          accountNumber: details.accountNumber,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data && response.data.success) {
        setAccountNameVerified(true);
        setDetails((prev) => ({
          ...prev,
          accountName: response.data.data.accountName || "Verified Account",
        }));
      } else {
        setError("Account verification failed");
      }
    } catch (error) {
      console.error("Failed to verify account:", error);
      setError("Failed to verify account details");
    } finally {
      setIsVerifyingAccount(false);
    }
  };

  // ---- handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    // ---- Handle numeric fields differently
    if (name === "accountNumber" || name === "bankCode") {
      let numericValue = value;

      // ---- Apply different max lengths based on field and country
      if (name === "accountNumber") {
        if (
          paymentMethod === PAYMENT_METHOD.MOBILE_MONEY ||
          details.iso2 === "NG"
        ) {
            const phoneNumberLength = fromCurrency?.paymentMethod?.phoneNumberLength ?? 10;
          numericValue = handleNumericInput(value, phoneNumberLength);
        } else {
          const maxLength = getBankCodeMaxLength(details.iso2);
          numericValue = handleNumericInput(value, maxLength);
        }
      } else if (name === "bankCode") {
        // ---- For US routing numbers (9 digits) or UK sort codes (6 digits) made it 10
        if (details.iso2 === "US") {
          numericValue = handleNumericInput(value, 10);
        } else if (details.iso2 === "GB") {
          numericValue = handleNumericInput(value, 10);
        } else if (details.iso2 === "CA") {
          numericValue = handleNumericInput(value, 10);
        } else {
          numericValue = handleNumericInput(value, 16);
        }
      }

      setDetails((prev) => ({
        ...prev,
        [name]: numericValue,
      }));

          if (name === "accountNumber" && accountNameVerified) {
      setAccountNameVerified(false);
      setDetails((prev) => ({
        ...prev,
        accountName: "",
      }));
    }

      return;
    }

    // ---- Normal handling for other fields
    setDetails((prev) => {
      const updatedDetails = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };

      if (name === "bankCode" && details.iso2 === "NG") {
        updatedDetails.providerName = value;
      }

      return updatedDetails;
    });

    // if (name === "accountNumber" && accountNameVerified) {
    //   setAccountNameVerified(false);
    //   setDetails((prev) => ({
    //     ...prev,
    //     accountName: "",
    //   }));
    // }
  };

  // ---- Helper function for numeric input validation
  const handleNumericInput = (value: string, maxLength?: number) => {
    // Remove all non-digit characters
    let numericValue = value.replace(/\D/g, "");

    // Apply max length if provided
    if (maxLength !== undefined) {
      numericValue = numericValue.slice(0, maxLength);
    }

    return numericValue;
  };

  // ---- sets the bank searches
  const handleBankSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBankSearchQuery(e.target.value);
    setShowBankDropdown(true);
  };

  // ---- handles selecting of banks passing the data
  const handleBankSelect = (bank: any) => {
    setDetails((prev) => ({
      ...prev,
      providerName: bank.name,
      bankCode: bank.code,
      paymentMethod: PAYMENT_METHOD.BANK_TRANSFER,
      accountName: "",
      accountNumber: "",
    }));
    setBankSearchQuery(bank.name);
    setShowBankDropdown(false);
    setAccountNameVerified(false);
  };

  // ---- removes a bank selected clearing all the selection data
  const handleRemoveSelectedBank = () => {
    setDetails((prev) => ({
      ...prev,
      providerName: "",
      bankCode: "",
      accountName: "",
      accountNumber: "",
    }));
    setBankSearchQuery("");
    setAccountNameVerified(false);
  };

  // ---- handle selecting of providers for mobile money
  const handleProviderSelect = (providerName: string) => {
    setDetails((prev) => ({
      ...prev,
      providerName,
      paymentMethod: paymentMethod || PAYMENT_METHOD.BANK_TRANSFER,
      ...(paymentMethod === PAYMENT_METHOD.MOBILE_MONEY && {
        network: providerName,
      }),
    }));
    setShowProviderDropdown(false);
  };

  // ---- debouncer delaying the verifaction of bank details a second after all input are inputted
  useEffect(() => {
    if (
      paymentMethod === PAYMENT_METHOD.BANK_TRANSFER &&
      details.iso2 === "NG" &&
      currency === "NGN" &&
      details.bankCode &&
      details.accountNumber.length >= 10 &&
      !accountNameVerified &&
      !isVerifyingAccount
    ) {
      const debounceTimer = setTimeout(() => {
        verifyBankAccount();
      }, 1000);

      return () => clearTimeout(debounceTimer);
    }
  }, [details.bankCode, details.accountNumber]);

  // ---- use for selecting available payment method if its availabe
  const handleSelectBeneficiary = (beneficiary: PaymentDetails) => {
    onSave(beneficiary);
    handleClose();
  };

  // ----  validates a Nigerian bank
  const validateNigerianBankTransfer = () => {
    if (!accountNameVerified) {
      setError("Please verify your account details");
      return false;
    }
    return true;
  };

  // ---- handles submits the details and also triggers the save of details
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (EMAIL_PAYMENT_METHODS.has(paymentMethod)) {
      if (!details.email) {
        setError("Please provide an email address");
        return;
      }
    } else if (
      paymentMethod === PAYMENT_METHOD.WECHAT_PAY ||
      paymentMethod === PAYMENT_METHOD.ALIPAY
    ) {
      if (!details.accountName || !details.phoneNumber) {
        setError("Please fill all required fields");
        return;
      }
    } else if (paymentMethod === PAYMENT_METHOD.STABLE_CURRENCY) {
      if (!details.walletAddress || !details.network) {
        setError("Please fill all required fields");
        return;
      }
    } else {
      if (!details.accountName || !details.accountNumber) {
        setError("Please fill all required fields");
        return;
      }
    }

    if (isBankTransferMethod(paymentMethod)) {
      if (details.iso2 === "NG" && currency === "NGN") {
        if (!validateNigerianBankTransfer()) return;
      } else if (details.iso2 === "US" || details.iso2 === "GB") {
        if (!details.bankCode) {
          setError(
            `Please provide ${
              details.iso2 === "US" ? "Routing No." : "Sort Code"
            }`,
          );
          return;
        }
      }
    } else if (paymentMethod === PAYMENT_METHOD.MOBILE_MONEY) {
      if (!details.providerName) {
        setError("Please select a mobile network");
        return;
      }
    }
const { saveDetails: _, holderCity, holderStreet, holderPostalCode, phoneNumber: _phoneNumber, ...payloadDetails } = details;

    let fullDetails: PaymentDetails;

    if (
      paymentMethod === PAYMENT_METHOD.WECHAT_PAY ||
      paymentMethod === PAYMENT_METHOD.ALIPAY
    ) {
      fullDetails = {
        accountName: details.accountName,
        phoneNumber: `+86${details.phoneNumber}`,
        currency: currency,
        paymentMethod: paymentMethod,
        iso2: details.iso2,
      };
    } else {
      fullDetails = {
        ...payloadDetails,
        currency: currency,
        paymentMethod: paymentMethod || PAYMENT_METHOD.BANK_TRANSFER,
        ...(paymentMethod === PAYMENT_METHOD.MOBILE_MONEY && {
          network: details.providerName,
        }),
        ...(isBankTransferMethod(paymentMethod) && {
          bankCode: details.bankCode,
          providerName: details.providerName?.trim() ? details.providerName : details.bankCode,
          ...(details.iso2 === "US" && { bankCodeLabel: "Routing No." }),
          ...(details.iso2 === "GB" && { bankCodeLabel: "Sort Code" }),
          ...((details.iso2 === "GB" || currency === "EUR") &&
  holderCity && holderStreet && holderPostalCode && {
  holderCity,
  holderStreet,
  holderPostalCode,
}),
        }),
        ...(paymentMethod === PAYMENT_METHOD.STABLE_CURRENCY && {
          network: details.network,
        }),
      };
    }

    // Save payment details if checkbox is checked
    if (details.saveDetails) {
      // savePaymentDetails(fullDetails);
         const saved = await savePaymentDetails(fullDetails);
  if (!saved) return;
    }

    onSave(fullDetails);
    handleClose();
  };

  // For comparing selected beneficiary
  const isSelected = (beneficiary: any) => {
    if (!compareSelected) return false;

    if (
      beneficiary.paymentMethod === PAYMENT_METHOD.MOBILE_MONEY ||
      isBankTransferMethod(beneficiary.paymentMethod)
    ) {
      return (
        compareSelected?.accountName === beneficiary.accountName &&
        compareSelected?.accountNumber === beneficiary.accountNumber &&
        compareSelected?.providerName === beneficiary.providerName
      );
    }

    if (
      beneficiary.paymentMethod === PAYMENT_METHOD.WECHAT_PAY ||
      beneficiary.paymentMethod === PAYMENT_METHOD.ALIPAY
    ) {
      return (
        compareSelected?.accountName === beneficiary.accountName &&
        compareSelected?.phoneNumber === beneficiary.phoneNumber
      );
    }

    if (EMAIL_PAYMENT_METHODS.has(beneficiary.paymentMethod)) {
      return (
        compareSelected?.accountName === beneficiary.accountName &&
        compareSelected?.email === beneficiary.email
      );
    }

    if (beneficiary.paymentMethod === PAYMENT_METHOD.STABLE_CURRENCY) {
      return (
        compareSelected?.walletAddress === beneficiary.walletAddress &&
        compareSelected?.network === beneficiary.network
      );
    }

    return false;
  };

  // ---- useEffect for auto-selecting single network
  useEffect(() => {
    if (
      isOpen &&
      paymentMethod === PAYMENT_METHOD.STABLE_CURRENCY &&
      exchangeRate?.paymentDetails
    ) {
      const stableCoinDetails = exchangeRate.paymentDetails.filter(
        (detail) => detail.paymentMethod === PAYMENT_METHOD.STABLE_CURRENCY,
      );

      // ---- Auto-select if there's only one network and no network is currently selected
      if (stableCoinDetails.length === 1 && !details.network) {
        setDetails((prev) => ({
          ...prev,
          network: getNetworkCode(stableCoinDetails[0].network),
        }));
      }
    }
  }, [isOpen, paymentMethod, exchangeRate, details.network]);

  // ---- FIlter beneficiaries base on payment method
  const filteredBeneficiaries = savedBeneficiaries.filter((beneficiary) => {
    const matchesPayment = beneficiary.paymentMethod === paymentMethod;
    if (paymentMethod === PAYMENT_METHOD.STABLE_CURRENCY) {
      const availableNetworks =
        exchangeRate?.paymentDetails
          ?.filter(
            (detail) => detail.paymentMethod === PAYMENT_METHOD.STABLE_CURRENCY,
          )
          .map((detail) => getNetworkCode(detail.network)) || [];

      // ---- Only show saved beneficiaries that match the available networks
      const beneficiaryNetwork = getNetworkCode(beneficiary.network);
      return matchesPayment && availableNetworks.includes(beneficiaryNetwork);
    }

    return matchesPayment;
  });

  // ---- Rendering of Nigeria bank field
  const renderNigerianBankTransferFields = () => (
    <>
      <div className="relative" ref={bankSearchRef}>
        <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
          Select Bank
        </label>
        {details.bankCode ? (
          <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg dark:bg-gray-800 dark:border-gray-700">
            <span className="font-medium">{details.providerName}</span>
            <button
              type="button"
              onClick={handleRemoveSelectedBank}
              className="text-gray-500 hover:text-gray-700"
            >
              <CloseIcon className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <>
            <input
              type="text"
              value={bankSearchQuery}
              onChange={handleBankSearchChange}
              onFocus={() => setShowBankDropdown(true)}
              className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              placeholder="Search for your bank"
              required
            />
            {showBankDropdown && filteredBanks.length > 0 && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto dark:bg-gray-800 dark:border-gray-700">
                {filteredBanks.map((bank) => (
                  <div
                    key={bank.code}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer dark:hover:bg-gray-700"
                    onClick={() => handleBankSelect(bank)}
                  >
                    {bank.name}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
          Account Number
          <span className="float-right text-xs text-gray-500">
            {details.accountNumber?.length}/10
          </span>
        </label>
        <input
          type="text"
          name="accountNumber"
          value={details.accountNumber}
          onChange={handleInputChange}
          className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
          placeholder="Enter account number"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={10}
          minLength={10}
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
          Account Name
        </label>
        <div className="relative">
          <input
            type="text"
            name="accountName"
            value={details.accountName}
            onChange={handleInputChange}
            className={`w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white ${
              accountNameVerified ? "bg-green-50 dark:bg-green-900/20" : ""
            }`}
            placeholder="Enter account name"
            required
            disabled={accountNameVerified}
          />
          {isVerifyingAccount && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <Spinner className="h-5 w-5 text-blue-500" />
            </div>
          )}
        </div>
        {accountNameVerified && (
          <p className="text-xs text-green-600 mt-1 dark:text-green-400">
            Account name verified
          </p>
        )}
      </div>
    </>
  );

  // ---- Rendering of International bank field
  const renderInternationalBankFields = () => {
    // ---- Get the field label and account number max length
    const {
      label: fieldLabel,
      maxLength: accountNumberMaxLength,
      isAlphaNumeric,
    } = INT_BANK_FIELD_CONFIG[details.iso2] || INT_BANK_FIELD_CONFIG.default;
    const showAddressFields = details.iso2 === "GB" || currency === "EUR";

    return (
      <>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
            {fieldLabel}
          </label>
          <input
            type="text"
            name="bankCode"
            value={details.bankCode || ""}
            onChange={handleInputChange}
            className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
            placeholder={`Enter ${fieldLabel}`}
            inputMode={isAlphaNumeric ? "text" : "numeric"}
            pattern={isAlphaNumeric ? undefined : "[0-9]*"}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
            Account Number
            <span className="float-right text-xs text-gray-500">
              {details.accountNumber?.length}/{accountNumberMaxLength}
            </span>
          </label>
          <input
            type="text"
            name="accountNumber"
            value={details.accountNumber}
            onChange={handleInputChange}
            className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
            placeholder="Enter account number"
            maxLength={accountNumberMaxLength}
            inputMode="numeric"
            pattern="[0-9]*"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
            Account Name
          </label>
          <input
            type="text"
            name="accountName"
            value={details.accountName}
            onChange={handleInputChange}
            className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
            placeholder="Enter account name"
            required
          />
        </div>

        {showAddressFields && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Bank{" "}
                <span className="text-gray-400 font-normal">(Optional)</span>
              </label>
              <input
                type="text"
                name="providerName"
                value={details.providerName}
                onChange={handleInputChange}
                className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                placeholder="Enter Bank (Optional)"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                City
              </label>
              <input
                type="text"
                name="holderCity"
                value={details.holderCity || ""}
                onChange={handleInputChange}
                className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                placeholder="Enter city"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Street
              </label>
              <input
                type="text"
                name="holderStreet"
                value={details.holderStreet || ""}
                onChange={handleInputChange}
                className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                placeholder="Enter street"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Postal Code
              </label>
              <input
                type="text"
                name="holderPostalCode"
                value={details.holderPostalCode || ""}
                onChange={handleInputChange}
                className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                placeholder="Enter Postal Code"
                required
              />
            </div>
          </>
        )}
      </>
    );
  };

  // ---- Rendering of mobile money field
  const renderMobileMoneyFields = () => {
    const phoneNumberLength = fromCurrency?.paymentMethod?.phoneNumberLength ?? 10;
    return(
    <>
      <div className="relative" ref={providerDropdownRef}>
        <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
          Select Network
        </label>
        <button
          type="button"
          className="w-full px-4 py-3 pr-10 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white flex items-center"
          onClick={() => {
            if (paymentNetworkError) {
              setIsCurrencyNotSupportedModalOpen(true); // 🔹 Show modal if error exists
            } else {
              setShowProviderDropdown(!showProviderDropdown);
            }
          }}
          disabled={loadingProviders}
        >
          {details.providerName ? (
            <>
              {providers.find((p) => p.name === details.providerName)?.logo && (
                <img
                  src={
                    providers.find((p) => p.name === details.providerName)?.logo
                  }
                  alt={details.providerName}
                  className="w-5 h-5 mr-2"
                />
              )}
              <span>{details.providerName}</span>
            </>
          ) : (
            <span className="text-gray-400">Select your mobile network</span>
          )}
          <ChevronDownIcon className="ml-auto h-5 w-5 text-gray-400" />
          {loadingProviders && (
            <div className="absolute right-8 top-1/2 transform -translate-y-1/2">
              <Spinner className="h-5 w-5 text-blue-500" />
            </div>
          )}
        </button>

        {/* Dropdown */}
        {showProviderDropdown && !paymentNetworkError && (
          <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-800 dark:border dark:border-gray-700 max-h-60 overflow-auto">
            {providers.map((provider) => (
              <div
                key={provider.code}
                className={`flex items-center px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer ${
                  details.providerName === provider.name
                    ? "bg-gray-100 dark:bg-gray-700"
                    : ""
                }`}
                onClick={() => handleProviderSelect(provider.name)}
              >
                {provider.logo && (
                  <img
                    src={provider.logo}
                    alt={provider.name}
                    className="w-5 h-5 mr-2"
                  />
                )}
                <span>{provider.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Other input fields */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
          Mobile Money Number
          <span className="float-right text-xs text-gray-500">
            {details.accountNumber?.length}/{phoneNumberLength}
          </span>
        </label>
        <input
          type="tel"
          name="accountNumber"
          value={details.accountNumber}
          onChange={handleInputChange}
          className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
          placeholder="Enter mobile number"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={phoneNumberLength}
          minLength={8}
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
          Mobile Money Name
        </label>
        <input
          type="text"
          name="accountName"
          value={details.accountName}
          onChange={handleInputChange}
          className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
          placeholder="Enter name on mobile money account"
          required
        />
      </div>

      {/* Show currency not supported modal  */}
      {isCurrencyNotSupportedModalOpen && (
        <Modal
          isOpen={isCurrencyNotSupportedModalOpen}
          onClose={() => setIsCurrencyNotSupportedModalOpen(false)}
          className="max-w-md"
        >
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-800 dark:text-white">
                Currency Not Supported
              </h3>
              <button
                onClick={() => setIsCurrencyNotSupportedModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <CloseIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-gray-700 dark:text-gray-300">
                {paymentNetworkError ||
                  "The selected currency is not supported for mobile money payments."}
              </p>

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  onClick={() => setIsCurrencyNotSupportedModalOpen(false)}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </>
  )
};

  const renderEmailFields = () => (
    <>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
          Account Name
        </label>
        <input
          type="text"
          name="accountName"
          value={details.accountName}
          onChange={handleInputChange}
          className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
          placeholder="Enter account name"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
          Email
        </label>
        <input
          type="email"
          name="email"
          value={details.email}
          onChange={handleInputChange}
          className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
          placeholder="Enter Email Address"
          required
        />
      </div>
    </>
  );

  const renderStableCoinFields = () => {
    const stableCoinDetails =
      exchangeRate?.paymentDetails?.filter(
        (detail) => detail.paymentMethod === PAYMENT_METHOD.STABLE_CURRENCY,
      ) || [];

    const hasMultipleNetworks = stableCoinDetails.length > 1;
    const hasSingleNetwork = stableCoinDetails.length === 1;
    const selectedNetwork = details.network;

    return (
      <>
        {/* Network Selection */}
        <div className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
            {hasMultipleNetworks ? "Select Network" : "Network"}
          </label>

          {hasSingleNetwork ? (
            <input
              type="text"
              value={
                getBlockchainName(
                  getNetworkCode(stableCoinDetails[0].network),
                ) || stableCoinDetails[0].network
              }
              readOnly
              disabled
              className="w-full px-3 py-2.5 text-sm bg-gray-100 border border-gray-200 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white cursor-not-allowed sm:px-4 sm:py-3 sm:text-base"
            />
          ) : (
            <div className="relative">
              <select
                name="network"
                value={details.network}
                onChange={(e) => {
                  setDetails((prev) => ({
                    ...prev,
                    network: e.target.value,
                    walletAddress: "",
                  }));
                }}
                className="w-full px-3 py-2.5 pr-8 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white appearance-none sm:px-4 sm:py-3 sm:pr-10 sm:text-base"
                required
              >
                <option value="">Select a network</option>
                {stableCoinDetails.map((detail) => (
                  <option
                    key={detail._id}
                    value={getNetworkCode(detail.network)}
                  >
                    {getBlockchainName(getNetworkCode(detail.network))}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-gray-700 dark:text-gray-300">
                <ChevronDownIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              </div>
            </div>
          )}
        </div>

        {/* Wallet Address */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
            Wallet Address
          </label>
          <input
            type="text"
            name="walletAddress"
            value={details.walletAddress}
            onChange={handleInputChange}
            className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
            placeholder="Provide us with the Address you are sending from"
            required
          />
        </div>

        {/* Warning Message - Show when network is selected */}
        {selectedNetwork && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg dark:bg-yellow-900/20 dark:border-yellow-800">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              ⚠️ Please use the{" "}
              <span className="font-semibold">
                {getBlockchainName(selectedNetwork)}
              </span>{" "}
              network for your payment. Sending via another network will result
              in loss of funds.
            </p>
          </div>
        )}

        {/* Info message for single network */}
        {hasSingleNetwork && !selectedNetwork && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-900/20 dark:border-blue-800">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              Only one network available:{" "}
              <span className="font-semibold">
                {getBlockchainName(
                  getNetworkCode(stableCoinDetails[0].network),
                )}
              </span>
            </p>
          </div>
        )}
      </>
    );
  };

  const renderAlipayWechatFields = () => (
    <>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
          Account Name
        </label>
        <input
          type="text"
          name="accountName"
          value={details.accountName}
          onChange={handleInputChange}
          className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
          placeholder="Enter account name"
          required
        />
      </div>
      {/* <div>
        <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
          Phone Number
        </label>
        <input
          type="text"
          name="phoneNumber"
          value={details.phoneNumber || ""}
          onChange={handleInputChange}
          className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
          placeholder="Enter Phone Number"
          required
        />
      </div>
       */}
            <div className="mb-6">
        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Phone Number
        </label>
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <span className="text-gray-500 dark:text-gray-400">+86</span>
          </div>
          <input
            type="tel"
            name="phoneNumber"
            value={details.phoneNumber || ""}
            onChange={handleInputChange}
            placeholder="Enter phone number"
            className="w-full rounded-lg border border-gray-200 bg-white pl-16 pr-4 py-3 text-gray-900 transition-colors focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:border-primary/50"
            maxLength={11}
          />
        </div>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        {`Enter a valid 11-digit Phone number (without +86).`}
        </p>
      </div>
    </>
  );

  const renderFormFields = () => {
    if (!paymentMethod) return null;

    if (EMAIL_PAYMENT_METHODS.has(paymentMethod)) {
      return renderEmailFields();
    }

    if (
      paymentMethod === PAYMENT_METHOD.WECHAT_PAY ||
      paymentMethod === PAYMENT_METHOD.ALIPAY
    ) {
      return renderAlipayWechatFields();
    }

    if (paymentMethod === PAYMENT_METHOD.MOBILE_MONEY) {
      return renderMobileMoneyFields();
    }

    if (paymentMethod === PAYMENT_METHOD.STABLE_CURRENCY) {
      return renderStableCoinFields();
    }

    if (isBankTransferMethod(paymentMethod)) {
      if (hasBankList) {
        return renderNigerianBankTransferFields();
      }

      if (details?.iso2 && INTERNATIONAL_BANK_ISO2.has(details?.iso2)) {
        return renderInternationalBankFields();
      }

      // Default bank transfer fields if no specific country is matched
      return (
        <>
          <div>
  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
    {INT_BANK_FIELD_CONFIG[details.iso2]?.label || "Bank Name"}
  </label>
  <input
    type="text"
    name="providerName"
    value={details.providerName}
    onChange={handleInputChange}
    className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
    placeholder={`Enter ${INT_BANK_FIELD_CONFIG[details.iso2]?.label || "bank name"}`}
    required
  />
</div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              Account Number
            </label>
            <input
              type="text"
              name="accountNumber"
              value={details.accountNumber}
              onChange={handleInputChange}
              className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              placeholder="Enter account number"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              Account Name
            </label>
            <input
              type="text"
              name="accountName"
              value={details.accountName}
              onChange={handleInputChange}
              className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              placeholder="Enter account name"
              required
            />
          </div>
        </>
      );
    }
    return null;
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} className="max-w-md">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-gray-800 dark:text-white">
              Pay From - {currency}
            </h3>
            <button
              onClick={handleClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <CloseIcon className="h-6 w-6" />
            </button>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner className="h-8 w-8 text-blue-500" />
            </div>
          ) : step === "select" ? (
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-gray-700 dark:text-gray-300">
                Select Payment Details
              </h4>

              {filteredBeneficiaries.length > 0 ? (
                <div className="space-y-3">
                  {filteredBeneficiaries.map((beneficiary, index) => {
                    return (
                      <div
                        key={index}
                        onClick={() => handleSelectBeneficiary(beneficiary)}
                        className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                          isSelected(beneficiary)
                            ? "border-blue-500 bg-blue-50 dark:bg-blue-900/30"
                            : "border-gray-200 hover:border-blue-300 dark:border-gray-700"
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-800 dark:text-white">
                              {beneficiary.accountName}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-300 break-words whitespace-normal">
                              {EMAIL_PAYMENT_METHODS.has(paymentMethod)
                                ? beneficiary.email
                                : beneficiary.paymentMethod ===
                                    PAYMENT_METHOD.STABLE_CURRENCY
                                  ? `${beneficiary.walletAddress} `
                                  : beneficiary.paymentMethod ===
                                        PAYMENT_METHOD.WECHAT_PAY ||
                                      beneficiary.paymentMethod ===
                                        PAYMENT_METHOD.ALIPAY
                                    ? `${beneficiary.phoneNumber}`
                                    : `${beneficiary.accountNumber} - ${beneficiary.providerName}`}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {beneficiary.paymentMethod ===
                              PAYMENT_METHOD.MOBILE_MONEY
                                ? "Mobile Money"
                                : isBankTransferMethod(
                                      beneficiary.paymentMethod,
                                    )
                                  ? PAYMENT_METHOD_LABELS[
                                      beneficiary.paymentMethod
                                    ] ?? "Bank Transfer"
                                  : beneficiary.paymentMethod ===
                                      PAYMENT_METHOD.WECHAT_PAY
                                    ? "WeChat Pay"
                                    : beneficiary.paymentMethod ===
                                        PAYMENT_METHOD.ALIPAY
                                      ? "Alipay"
                                      : beneficiary.paymentMethod ===
                                          PAYMENT_METHOD.STABLE_CURRENCY
                                        ? `${getBlockchainName(
                                            beneficiary.network || "",
                                          )}`
                                        : EMAIL_PAYMENT_METHODS.has(
                                              beneficiary.paymentMethod,
                                            )
                                          ? `${beneficiary.paymentMethod}`
                                          : beneficiary.paymentMethod}
                            </p>
                          </div>

                          <div className="flex items-center flex-shrink-0">
                            {isSelected(beneficiary) && (
                              <CircleCheck className="text-blue-500 mr-2 h-5 w-5" />
                            )}
                            <button
                              onClick={(e) => openDeleteModal(beneficiary, e)}
                              className="text-gray-400 hover:text-red-500"
                            >
                              <TrashBinIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="border rounded-lg p-4 text-center text-gray-500 dark:text-gray-400">
                  {`No saved payment details for ${
                    paymentMethod === PAYMENT_METHOD.MOBILE_MONEY
                      ? "Mobile Money"
                      : isBankTransferMethod(paymentMethod)
                        ? PAYMENT_METHOD_LABELS[paymentMethod] ?? "Bank Transfer"
                        : EMAIL_PAYMENT_METHODS.has(paymentMethod)
                          ? `${paymentMethod}`
                          : paymentMethod === PAYMENT_METHOD.STABLE_CURRENCY
                            ? "Stable Currency"
                            : paymentMethod
                  }`}
                </div>
              )}

              <button
                onClick={() => setStep("form")}
                className="w-full py-3 px-4 bg-[#EB2E67] text-white font-medium rounded-lg mt-4"
              >
                Add New Payment Detail
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <h4 className="text-lg font-medium text-gray-700 dark:text-gray-300">
                  {paymentMethod === PAYMENT_METHOD.MOBILE_MONEY
                    ? "Mobile Money Details"
                    : isBankTransferMethod(paymentMethod)
                      ? "Bank Details"
                      : EMAIL_PAYMENT_METHODS.has(paymentMethod)
                        ? `${paymentMethod} Details`
                        : paymentMethod === PAYMENT_METHOD.STABLE_CURRENCY
                          ? "Stable Currency Details"
                          : "Pay From Details"}
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {paymentMethod === PAYMENT_METHOD.MOBILE_MONEY
                    ? "Provide us with your mobile money details"
                    : isBankTransferMethod(paymentMethod)
                      ? "Provide us with your bank details"
                      : EMAIL_PAYMENT_METHODS.has(paymentMethod)
                        ? `Provide us with your ${paymentMethod} details`
                        : "Provide us with your details"}
                </p>

                {error && (
                  <div className="p-2 text-red-600 bg-red-50 rounded-md text-sm">
                    {error}
                  </div>
                )}

                {renderFormFields()}

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="saveDetails"
                    checked={details.saveDetails || false}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    id="saveDetails"
                  />
                  <label
                    htmlFor="saveDetails"
                    className="ml-2 block text-sm text-gray-700 dark:text-gray-300"
                  >
                    Save this payment Detail
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={resetForm}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      paymentMethod === "BANK_TRANSFER" &&
                      details.iso2 === "NG" &&
                      currency === "NGN" &&
                      !accountNameVerified
                    }
                  >
                    {isVerifyingAccount ? "Verifying..." : "Continue"}
                  </Button>
                </div>
              </div>
            </form>
          )}
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        className="max-w-md"
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-gray-800 dark:text-white">
              Delete Payment Method
            </h3>
            <button
              onClick={() => setIsDeleteModalOpen(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <CloseIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="space-y-4">
            <p className="text-gray-700 dark:text-gray-300">
              Are you sure you want to delete this payment method?
            </p>
            {paymentMethodToDelete && (
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                <p className="font-medium text-gray-800 dark:text-white">
                  {paymentMethodToDelete.accountName}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300 break-words whitespace-normal">
                  {EMAIL_PAYMENT_METHODS.has(
                    paymentMethodToDelete.paymentMethod,
                  )
                    ? paymentMethodToDelete.email
                    : paymentMethodToDelete.paymentMethod ===
                        PAYMENT_METHOD.WECHAT_PAY
                      ? `${paymentMethodToDelete.phoneNumber ?? ""} - WeChat Pay`
                      : paymentMethodToDelete.paymentMethod ===
                          PAYMENT_METHOD.ALIPAY
                        ? `${paymentMethodToDelete.phoneNumber ?? ""} - Alipay`
                        : paymentMethodToDelete.paymentMethod ===
                            PAYMENT_METHOD.STABLE_CURRENCY
                          ? `${paymentMethodToDelete.walletAddress ?? ""} - ${paymentMethodToDelete.network ?? ""}`
                          : `${paymentMethodToDelete.accountNumber ?? ""} - ${paymentMethodToDelete.providerName ?? ""}`}
                </p>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDeleteModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={deletePaymentMethod}
                disabled={isDeleting}
                className="bg-red-600 hover:bg-red-700"
              >
                {isDeleting ? (
                  <Spinner className="h-5 w-5 text-white" />
                ) : (
                  "Delete"
                )}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}
